<template>
  <div id="app_dashboard_admin">
    <v-container fluid>
      <v-row align="center" justify="center">
        
        <!-- Change Summary Table -->
        <v-col cols="12" md="6">
          <v-card class="mx-auto custom_card_box_dashboard" elevation="0" border="sm opacity-1" rounded="lg">
            <v-card flat>
              <template v-slot:title>
                <p class="ml-5 font-weight-bold custom_page_component_main_title">COLLECTION BREAKDOWN</p>
              </template>
              <template v-slot:subtitle>
                <p class="ml-5 custom_page_main_sub_title_very_small_red" v-if="dashboardData">
                  Total: LKR {{ formatCurrency(dashboardData.totalChangeAmount || 0) }}
                </p>
              </template>
              <template v-slot:append>
                <v-btn
                  class="mr-4"
                  variant="outlined"
                  style="color: #C626DE; border-color: #C626DE;"
                  @click="exportExcel('change')"
                >
                  <v-icon size="large" color="#C626DE">mdi-file-excel</v-icon>
                  &nbsp;&nbsp;Excel
                </v-btn>
              </template>
            </v-card>

            <v-divider></v-divider>

            <v-card-text>
              <v-data-table 
                :headers="changeHeaders" 
                :items="changeData" 
                class="mt-4 custom_table_normal" 
                hide-default-footer
                density="compact"
              >
                <template v-slot:[`item.value`]="{ item }">
                  <div class="font-weight-bold" :class="item.negative ? 'text-error' : 'text-primary'">
                    LKR {{ formatCurrency(item.value) }}
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ERP Comparison Table -->
        <v-col cols="12" md="6">
          <v-card class="mx-auto custom_card_box_dashboard" elevation="0" border="sm opacity-1" rounded="lg">
            <v-card flat>
              <template v-slot:title>
                <p class="ml-5 font-weight-bold custom_page_component_main_title">ERP RECONCILIATION</p>
              </template>
              <template v-slot:subtitle>
                <p class="ml-5 custom_page_main_sub_title_very_small_red" v-if="dashboardData">
                  ERP Total: LKR {{ formatCurrency(dashboardData.erpAmount || 0) }}
                </p>
              </template>
              <template v-slot:append>
                <v-btn
                  class="mr-4"
                  variant="outlined"
                  style="color: #C626DE; border-color: #C626DE;"
                  @click="exportExcel('erp')"
                >
                  <v-icon size="large" color="#C626DE">mdi-file-excel</v-icon>
                  &nbsp;&nbsp;Excel
                </v-btn>
              </template>
            </v-card>

            <v-divider></v-divider>

            <v-card-text>
              <v-data-table 
                :headers="erpHeaders" 
                :items="erpData" 
                class="mt-4 custom_table_normal" 
                hide-default-footer
                density="compact"
              >
                <template v-slot:[`item.value`]="{ item }">
                  <div class="font-weight-bold" :class="item.negative ? 'text-error' : ''">
                    LKR {{ formatCurrency(item.value) }}
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>

      </v-row>
    </v-container>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import axios from 'axios';

export default {
  name: 'TopSellingProducts',
  
  props: {
    dashboardData: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      changeHeaders: [
        { title: 'METRIC', key: 'label', align: 'start' },
        { title: 'VALUE', key: 'value', align: 'end' },
      ],
      erpHeaders: [
        { title: 'METRIC', key: 'label', align: 'start' },
        { title: 'VALUE', key: 'value', align: 'end' },
      ],
    };
  },

  computed: {
    changeData() {
      if (!this.dashboardData) return [];
      const d = this.dashboardData;
      
      return [
        {
          label: 'Total Change Amount',
          value: d.totalChangeAmount || 0,
        },
        {
          label: 'Remaining to Collect',
          value: d.totalChangeRemainingAmount || 0,
        },
        {
          label: 'Assigned Amount',
          value: d.totalChangeAssignedAmount || 0,
          negative: (d.totalChangeAssignedAmount || 0) < 0,
        },
        {
          label: 'Actual Remaining',
          value: d.totalChangeActualRemainingAmount || 0,
        },
      ];
    },

    erpData() {
      if (!this.dashboardData) return [];
      const d = this.dashboardData;
      
      return [
        { label: 'ERP Amount', value: d.erpAmount || 0 },
        { label: 'ERP Remaining', value: d.erpRemainingAmount || 0 },
        { label: 'ERP Assigned', value: d.erpAssignedAmount || 0, negative: (d.erpAssignedAmount || 0) < 0 },
        { label: 'ERP Actual Remaining', value: d.erpActualRemainingAmount || 0 },
      ];
    }
  },

  methods: {
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    },

    exportExcel(type) {
      let data, fileName, sheetName;
      
      if (type === 'change') {
        data = this.changeData.map(item => ({
          'METRIC': item.label,
          'VALUE': item.value,
        }));
        fileName = `change_summary_${new Date().toISOString().split('T')[0]}.xlsx`;
        sheetName = 'Change Summary';
      } else {
        data = this.erpData.map(item => ({
          'METRIC': item.label,
          'VALUE': item.value,
        }));
        fileName = `erp_summary_${new Date().toISOString().split('T')[0]}.xlsx`;
        sheetName = 'ERP Summary';
      }

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      XLSX.writeFile(workbook, fileName);
      
      this.logDownload(fileName);
    },

    async logDownload(fileName) {
      try {
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId');
        await axios.post(`/api/log-document-download`, {
          user_id: userId,
          file_type: 'excel',
          file_name: fileName,
          entity_area: 'Dashboard',
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error logging download:', error);
      }
    },
  }
};
</script>

<style>
@import "../../styles/dashboard/dashboard.scss";
</style>